import React, { useEffect, useState } from 'react';
import { X, Clock, Check, AlertTriangle } from 'lucide-react';
import { Button } from './ui/Button';
import { useCurrencyConversion } from '../hooks/useCurrencyConversion';
interface UpsellPopupProps {
  isOpen: boolean;
  onClose: () => void;
}
interface UpsellOption {
  id: 'system360' | 'recipes' | 'therapy';
  title: string;
  price: number;
  originalPrice: number;
  description: string;
  image: string;
  color: string;
  bgColor: string;
  borderColor: string;
}
const upsellOptions: UpsellOption[] = [{
  id: 'system360',
  title: 'Sistema de Desarrollo Infantil 360°',
  price: 9.99,
  originalPrice: 29.99,
  description: '+13,000 actividades listas para imprimir (2-13 años)',
  image: 'https://cdn.magicpatterns.com/uploads/hPiSzVERYNzZrP5PXgdyYT/Captura_de_pantalla_2026-02-07_a_la(s)_12.04.29_p._m..png',
  color: 'text-[#3B82F6]',
  bgColor: 'bg-[#EFF6FF]',
  borderColor: 'border-[#3B82F6]'
}, {
  id: 'recipes',
  title: 'Recetario Infantil Saludable',
  price: 9.99,
  originalPrice: 29.99,
  description: '+200 recetas rápidas y saludables para tu peque',
  image: 'https://cdn.magicpatterns.com/uploads/ipAwGze64hX7oEGvVzhd1h/Captura_de_pantalla_2026-02-07_a_la(s)_12.08.35_p._m..png',
  color: 'text-[#A855F7]',
  bgColor: 'bg-[#FAF5FF]',
  borderColor: 'border-[#A855F7]'
}, {
  id: 'therapy',
  title: 'Kit de Terapia para los Peques',
  price: 12.0,
  originalPrice: 34.99,
  description: 'Herramientas para emociones y calma',
  image: 'https://cdn.magicpatterns.com/uploads/gpnExC61za1PkTQ6ARYdjG/Captura_de_pantalla_2026-02-07_a_la(s)_12.15.13_p._m..png',
  color: 'text-[#D97706]',
  bgColor: 'bg-[#FFFBEB]',
  borderColor: 'border-[#D97706]'
}];
export function UpsellPopup({
  isOpen,
  onClose
}: UpsellPopupProps) {
  const [selectedUpsells, setSelectedUpsells] = useState<string[]>([]);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes in seconds
  const {
    isLocal,
    localSymbol,
    localCode,
    countryFlag,
    convert
  } = useCurrencyConversion();
  const basePrice = 16.39;
  useEffect(() => {
    if (!isOpen) return;
    const timer = setInterval(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    return () => clearInterval(timer);
  }, [isOpen]);
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  const toggleUpsell = (id: string) => {
    setSelectedUpsells(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
  };
  const calculateTotal = () => {
    const upsellTotal = selectedUpsells.reduce((sum, id) => {
      const option = upsellOptions.find(opt => opt.id === id);
      return sum + (option?.price || 0);
    }, 0);
    return basePrice + upsellTotal;
  };
  const handleAccept = () => {
    const hasSystem360 = selectedUpsells.includes('system360');
    const hasRecipes = selectedUpsells.includes('recipes');
    const hasTherapy = selectedUpsells.includes('therapy');
    let url = 'https://mundomamis.com/cart/43938138390637:1'; // Base URL
    if (hasSystem360 && hasRecipes && hasTherapy) {
      url = 'https://mundomamis.com/cart/43963153743981:1';
    } else if (hasSystem360 && hasTherapy) {
      url = 'https://mundomamis.com/cart/43963198767213:1';
    } else if (hasSystem360 && hasRecipes) {
      url = 'https://mundomamis.com/cart/43963153481837:1';
    } else if (hasRecipes && hasTherapy) {
      url = 'https://mundomamis.com/cart/43963170062445:1';
    } else if (hasSystem360) {
      url = 'https://mundomamis.com/cart/43963153023085:1';
    } else if (hasRecipes) {
      url = 'https://mundomamis.com/cart/43963153907821:1';
    } else if (hasTherapy) {
      url = 'https://mundomamis.com/cart/43963170291821:1';
    }
    // Fire Meta Pixel event
    if (window.fbq) {
      window.fbq('track', 'AddToCart', {
        content_name: 'Kit Anti-Berrinches + Upsells',
        content_type: 'product',
        value: calculateTotal(),
        currency: 'USD'
      });
    }
    window.location.href = url;
  };
  const handleReject = () => {
    // Redirect to base kit only
    window.location.href = 'https://mundomamis.com/cart/43938138390637:1';
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-494">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-300 relative" data-id="element-495">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-100 p-4 flex items-center justify-between" data-id="element-496">
          <div className="flex items-center gap-2" data-id="element-497">
            <AlertTriangle className="w-5 h-5 text-[#E8845F]" data-id="element-498" />
            <span className="font-bold text-gray-800" data-id="element-499">
              ¡Espera! Oferta única
            </span>
          </div>
          <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full font-mono font-bold text-sm ${timeLeft < 60 ? 'bg-red-100 text-red-600 animate-pulse' : 'bg-orange-100 text-[#E8845F]'}`} data-id="element-500">
            <Clock className="w-4 h-4" data-id="element-501" />
            {formatTime(timeLeft)}
          </div>
        </div>

        <div className="p-5 space-y-4" data-id="element-502">
          <p className="text-sm text-gray-600 text-center mb-2" data-id="element-503">
            Completa tu kit con estas herramientas exclusivas a precio especial.
            <br data-id="element-504" />
            <span className="font-bold text-[#E8845F]" data-id="element-505">
              Esta oferta no se repetirá.
            </span>
          </p>

          {/* Upsell Options */}
          <div className="space-y-3" data-id="element-506">
            {upsellOptions.map(option => {
            const isSelected = selectedUpsells.includes(option.id);
            return <div key={option.id} onClick={() => toggleUpsell(option.id)} className={`relative flex items-start gap-3 p-3 rounded-xl border-2 transition-all cursor-pointer ${isSelected ? `${option.borderColor} ${option.bgColor}` : 'border-gray-200 border-dashed hover:border-gray-300'}`} data-id="element-507">
                  {/* Checkbox */}
                  <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 mt-1 transition-colors ${isSelected ? `bg-current ${option.color} border-transparent` : 'border-gray-300 bg-white'}`} data-id="element-508">
                    {isSelected && <Check className="w-3.5 h-3.5 text-white" data-id="element-509" />}
                  </div>

                  {/* Image */}
                  <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 border border-black/5" data-id="element-510">
                    <img src={option.image} alt={option.title} className="w-full h-full object-cover" data-id="element-511" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0" data-id="element-512">
                    <h4 className="font-bold text-gray-900 text-sm leading-tight" data-id="element-513">
                      {option.title}
                    </h4>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2" data-id="element-514">
                      {option.description}
                    </p>
                    <div className="flex items-baseline gap-2 mt-1.5" data-id="element-515">
                      <span className="text-xs text-gray-400 line-through" data-id="element-516">
                        ${option.originalPrice}
                      </span>
                      <span className={`text-sm font-bold ${option.color}`} data-id="element-517">
                        +${option.price.toFixed(2)}
                      </span>
                      {isLocal && <span className="text-[10px] text-gray-400" data-id="element-518">
                          ({localSymbol}
                          {convert(option.price)})
                        </span>}
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-100 p-4 space-y-3 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]" data-id="element-519">
          <div className="flex justify-between items-center text-sm" data-id="element-520">
            <span className="text-gray-500" data-id="element-521">Total a pagar:</span>
            <div className="text-right" data-id="element-522">
              <span className="block font-extrabold text-xl text-gray-900" data-id="element-523">
                ${calculateTotal().toFixed(2)} USD
              </span>
              {isLocal && <span className="block text-xs text-[#4AAAA5] font-medium" data-id="element-524">
                  ≈ {localSymbol}
                  {convert(calculateTotal())} {localCode}
                </span>}
            </div>
          </div>

          <Button onClick={handleAccept} className="w-full text-lg shadow-xl bg-gradient-to-r from-[#E8845F] to-[#F5A574]" size="lg" data-id="element-525">
            {selectedUpsells.length > 0 ? 'Sí, agregar a mi orden' : 'Continuar con el Kit'}
          </Button>

          <button onClick={handleReject} className="w-full text-center text-xs text-gray-400 hover:text-gray-600 underline decoration-gray-300 underline-offset-2 transition-colors" data-id="element-526">
            No gracias, solo quiero el Kit Anti-Berrinches por $16.39
          </button>
        </div>
      </div>
    </div>;
}